import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { PlanInfo } from "@/lib/upsell";

interface RawPlan {
  name?: string;
  tier?: string;
  displayName?: string;
  monthlyPrice?: number | string;
  monthlyTokenLimit?: number | string;
  features?: string[] | string;
}

const toNum = (v: unknown) => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? 0));
  return Number.isFinite(n) ? n : 0;
};

const toFeatures = (f: RawPlan["features"]): string[] => {
  if (Array.isArray(f)) return f;
  try {
    const parsed = JSON.parse(String(f ?? "[]"));
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/** Public pricing plans (cached; they rarely change). */
export function usePlans(enabled = true) {
  return useQuery({
    queryKey: ["billing", "plans"],
    enabled,
    staleTime: 60 * 60 * 1000,
    queryFn: async (): Promise<PlanInfo[]> => {
      const r = await api.get("/billing/plans");
      const rows: RawPlan[] = Array.isArray(r.plans) ? r.plans : Array.isArray(r.data) ? r.data : [];
      return rows.map((p) => ({
        name: p.name || "",
        tier: p.tier || p.name || "",
        displayName: p.displayName || p.name || "",
        monthlyPrice: toNum(p.monthlyPrice),
        monthlyTokenLimit: toNum(p.monthlyTokenLimit),
        features: toFeatures(p.features),
      }));
    },
  });
}
